/**
 * ActivityHeatmap — compact calendar heatmap showing the rhythm of project activity.
 *
 * Renders 8 weeks × 7 days. Each cell is colored by event density.
 * Clicking a day applies a date filter to the changelog feed.
 *
 * Design principle: "The changelog should make the rhythm of a project visible —
 * who's touching what, when things tend to break."
 */

import type { ChangeEvent } from "../../api/changelog";

interface Props {
  events: ChangeEvent[];
  /** Called when the user clicks a day cell; passes ISO date string "YYYY-MM-DD". */
  onDaySelect: (date: string | null) => void;
  /** Currently active date filter, if any. */
  activeDateFilter: string | null;
}

const DAYS = ["S", "M", "T", "W", "T", "F", "S"];
const WEEKS = 8;

/** Return "YYYY-MM-DD" string in local time. */
function toDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Intensity → CSS color. Loosely modelled on GitHub's contribution palette. */
function cellColor(count: number, isActive: boolean): string {
  if (isActive) return "var(--accent-9)";
  if (count === 0) return "var(--gray-a4)";
  if (count <= 2) return "var(--accent-a5)";
  if (count <= 5) return "var(--accent-a7)";
  return "var(--accent-a9)";
}

export function ActivityHeatmap({ events, onDaySelect, activeDateFilter }: Props) {
  // Build a map: date-key → { total, byType }
  const dateMap = new Map<string, { total: number; byType: Record<string, number> }>();
  for (const e of events) {
    const key = e.created_at.slice(0, 10); // "YYYY-MM-DD"
    if (!dateMap.has(key)) dateMap.set(key, { total: 0, byType: {} });
    const entry = dateMap.get(key)!;
    entry.total++;
    entry.byType[e.event_type] = (entry.byType[e.event_type] ?? 0) + 1;
  }

  // Build grid: WEEKS rows of 7 days, ending today.
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  // Start on the nearest Sunday at or before (WEEKS * 7 - 1) days ago.
  const totalDays = WEEKS * 7;
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - (totalDays - 1));
  // Shift back to Sunday.
  startDate.setDate(startDate.getDate() - startDate.getDay());

  const grid: { date: Date; key: string }[][] = [];
  for (let w = 0; w < WEEKS; w++) {
    const week: { date: Date; key: string }[] = [];
    for (let d = 0; d < 7; d++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + w * 7 + d);
      week.push({ date, key: toDateKey(date) });
    }
    grid.push(week);
  }

  // Show month labels at week boundaries.
  const monthLabels: { weekIndex: number; label: string }[] = [];
  for (let w = 0; w < WEEKS; w++) {
    const firstDay = grid[w]![0]!.date;
    if (w === 0 || firstDay.getDate() <= 7) {
      monthLabels.push({
        weekIndex: w,
        label: firstDay.toLocaleDateString(undefined, { month: "short" }),
      });
    }
  }

  const CELL = 11;
  const GAP = 2;

  return (
    <div style={{ padding: "8px 0 4px" }}>
      {/* Day-of-week labels */}
      <div style={{ display: "flex", gap: GAP, marginLeft: 28, marginBottom: 2 }}>
        {DAYS.map((d, i) => (
          <div
            key={i}
            style={{ width: CELL, fontSize: 9, color: "var(--gray-9)", textAlign: "center" }}
          >
            {i % 2 === 1 ? d : ""}
          </div>
        ))}
      </div>

      {/* Grid */}
      <div style={{ display: "flex", gap: GAP }}>
        {/* Month labels column */}
        <div style={{ width: 24, display: "flex", flexDirection: "column", gap: GAP }}>
          {grid.map((_week, wi) => {
            const label = monthLabels.find((m) => m.weekIndex === wi);
            return (
              <div key={wi} style={{ height: CELL, fontSize: 9, color: "var(--gray-9)", lineHeight: `${CELL}px` }}>
                {label?.label ?? ""}
              </div>
            );
          })}
        </div>

        {/* Day cells */}
        {grid.map((week, wi) => (
          <div key={wi} style={{ display: "flex", flexDirection: "column", gap: GAP }}>
            {week.map(({ date, key }) => {
              const isFuture = date > today;
              const entry = dateMap.get(key);
              const count = entry?.total ?? 0;
              const isActive = activeDateFilter === key;

              const tooltipLines = entry
                ? [
                    date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }),
                    `${count} event${count !== 1 ? "s" : ""}`,
                    ...Object.entries(entry.byType).map(([t, n]) => `  ${t.replace(/_/g, " ")}: ${n}`),
                  ]
                : [date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }), "No events"];

              return (
                <div
                  key={key}
                  title={tooltipLines.join("\n")}
                  onClick={() => {
                    if (isFuture || count === 0) return;
                    onDaySelect(isActive ? null : key);
                  }}
                  style={{
                    width: CELL,
                    height: CELL,
                    borderRadius: 2,
                    background: isFuture ? "transparent" : cellColor(count, isActive),
                    cursor: count > 0 && !isFuture ? "pointer" : "default",
                    opacity: isFuture ? 0 : 1,
                    outline: isActive ? "1px solid var(--accent-11)" : undefined,
                  }}
                />
              );
            })}
          </div>
        ))}
      </div>

      {activeDateFilter && (
        <div
          style={{ marginTop: 4, fontSize: 10, color: "var(--accent-11)", cursor: "pointer" }}
          onClick={() => onDaySelect(null)}
        >
          Filtered to {activeDateFilter} — click to clear
        </div>
      )}
    </div>
  );
}
